import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Query } from "react-apollo";
import { gql } from "apollo-boost";
import orderBy from "lodash/orderBy";

import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";

import BasicCard from "../BasicCard";
import Dropdown from "../Dropdown";
import Loader from "../Loader";

import { hasImages } from "../../helpers";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    [theme.breakpoints.up("md")]: {
      display: "flex",
      justifyContent: "center"
    }
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  grid: {
    [theme.breakpoints.up("md")]: {
      justifyContent: "center"
    }
  },
  comment: {
    marginBottom: theme.spacing(3),
    maxWidth: "900px",
    margin: "0 auto"
  }
}));

function formatOptions(types, t) {
  return orderBy(
    types.map(({ id, name }) => ({
      id,
      name,
      translation: t(`names.types.${name}`)
    })),
    "translation",
    "asc"
  );
}

function ChooseType({
  focus,
  materialId,
  onReturn,
  onSelect,
  productId,
  selectedType,
  setSelectedType,
  systemId
}) {
  const [currentType, setCurrentType] = useState(null);
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Query query={TYPES_QUERY} variables={{ materialId, productId, systemId }}>
      {({ data, loading, error, refetch }) => {
        if (loading) {
          return (
            <div className="flex w-100">
              <Loader />
            </div>
          );
        }

        if (error) {
          return (
            <div className="flex w-100">
              <div>{t("root.error")}</div>
            </div>
          );
        }

        return (
          <>
            <h2>{t("categories.type")}</h2>
            <div className={classes.comment}>
              {t("creator.hint.type")}
            </div>
            <div className={classes.root}>
              {!currentType ? (
                hasImages(data.types) ? (
                  <Grid className={classes.grid} container spacing={3}>
                    {data.types.map(({ id, name, image, subtypes }) => (
                      <Grid item>
                        <BasicCard
                          id={id}
                          image={image && `/img/types/${image}`}
                          name={t(`names.types.${name}`)}
                          onSelect={() => {
                            if (subtypes && subtypes.length) {
                              setCurrentType({ subtypes, id, name });
                            } else {
                              setSelectedType({
                                id,
                                name,
                                image: image && `/img/types/${image}`,
                                isSubtype: false
                              });
                              onSelect();
                            }
                          }}
                          selected={
                            subtypes && subtypes.length
                              ? currentType && currentType.id === id
                              : selectedType &&
                                selectedType.id &&
                                selectedType.id === id
                          }
                          grayscale
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Dropdown
                    focus={focus}
                    options={formatOptions(data.types, t)}
                    label={t("categories.type")}
                    onChange={newValue => {
                      setSelectedType({
                        id: newValue.id,
                        name: newValue.name,
                        image: null,
                        isSubtype: false
                      });
                      onSelect();
                    }}
                    translationKey="names.types"
                    value={selectedType}
                  />
                )
              ) : (
                <Grid className={classes.grid} container spacing={3}>
                  <Grid item>
                    <BasicCard
                      label={
                        <Button startIcon={<ArrowBackIcon />}>
                          {t("creator.undo")}
                        </Button>
                      }
                      onSelect={() => {
                        setCurrentType(null);
                        setSelectedType(null);
                        if (onReturn) onReturn();
                      }}
                    />
                  </Grid>
                  {currentType.subtypes.map(({ id, name, image }) => (
                    <Grid item>
                      <BasicCard
                        id={id}
                        image={image && `/img/subtypes/${image}`}
                        name={t(`names.subtypes.${name}`)}
                        onSelect={() => {
                          setSelectedType({
                            id,
                            name,
                            image: image && `/img/subtypes/${image}`,
                            isSubtype: true
                          });
                          onSelect();
                        }}
                        selected={
                          selectedType &&
                          selectedType.id &&
                          selectedType.id === id
                        }
                        grayscale
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </div>
          </>
        );
      }}
    </Query>
  );
}

export const TYPES_QUERY = gql`
  query TypesQuery($materialId: ID!, $productId: ID!, $systemId: ID!) {
    types(materialId: $materialId, productId: $productId, systemId: $systemId) {
      id
      name
      image
      subtypes {
        id
        name
        image
      }
    }
  }
`;

export default ChooseType;
